import React, { useState, useEffect, useCallback, useContext, ReactNode } from "react";
import { useApolloClient } from "@apollo/client";
import cx from "classnames";
import { IParams } from "@models/IParams";
import { useParams } from "react-router-dom";
import { SelectPagination } from "@foris/avocado-suite";
import { BookingContext } from "../../context/BookingContext";
import { Types } from "../../context/search.reducer";
import { DataQuery, PageInfo } from "@models/ISchema";
import { programsQuery } from "@services/collections/programs/graphql/programs.query";
import { getProgramSelectorLabel } from "@modules/booking/utils/programs";
import { useTranslation } from "react-i18next";
import css from "../Preferences/preferences.module.scss";

interface Props {
  className?: string;
  escenarioId?: string;
  isRequired?: boolean;
  maxMenuHeight?: number;
  initialValue?: { value: string | number; label: ReactNode };
}

const ProgramSelect = ({
  className = "",
  initialValue,
  escenarioId,
  isRequired = false,
  maxMenuHeight,
}: Props) => {
  const { t } = useTranslation();
  const { scenario }: IParams = useParams();
  const { state, dispatch } = useContext(BookingContext);
  const client = useApolloClient();
  const defaultProgram =
    typeof state?.search?.preferences?.program === "object"
      ? {
          label: getProgramSelectorLabel(state?.search?.preferences?.program),
          value: state?.search?.preferences?.program?.id,
        }
      : { label: state?.search?.preferences?.program };
  const [programPage, setProgramPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [optionsProgram, setOptionsProgram] = useState(null);
  const [valueProgram, setValueProgram] = useState(initialValue || defaultProgram);
  const [termToSearch, setTermToSearch] = useState("");

  const getPrograms = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;
      setLoading(true);

      try {
        const variables = {
          query: programsQuery,
          variables: {
            scenarioId: escenarioId || scenario,
            filter: {
              pagination: {
                page,
                size: optionsPerPage,
                searchTerm,
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery: DataQuery = data?.data;
        const pageInfo: PageInfo = dataQuery?.programs?.pageInfo;
        const programs = dataQuery?.programs;

        const options = [];

        if (programs) {
          programs.items.forEach(program => {
            options.push({
              label: getProgramSelectorLabel(program),
              value: program.id,
            });
          });
        }

        setOptionsProgram(options);

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        setLoading(false);
        return null;
      }
    },
    [client],
  );

  const loadProgramOptions = async (searchTerm: string) => {
    let searchPage = 1;

    if (termToSearch === searchTerm) {
      searchPage = programPage;
    } else {
      setTermToSearch(searchTerm);
    }
    const { pageInfo, options: programOptions } = await getPrograms(searchTerm, searchPage);
    setProgramPage(programPage + 1);
    return {
      options: programOptions,
      hasMore: pageInfo?.hasNextPage,
      additional: {
        page: programPage,
      },
    };
  };

  useEffect(() => {
    if (valueProgram?.value) {
      dispatch({
        type: Types.SetPreferences,
        payload: {
          ...state?.search?.preferences,
          program: valueProgram,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!optionsProgram && !loading) {
      getPrograms();
    } else if (
      optionsProgram?.length &&
      !loading &&
      state?.search?.preferences?.program &&
      !valueProgram
    ) {
      const programLabel: string = state?.search?.preferences?.program?.label;
      const program = optionsProgram.find(
        (optionProgram: any) => optionProgram?.label === programLabel,
      );
      setValueProgram(program);
    }
  }, [optionsProgram]);

  const preferences = { ...state?.search?.preferences };

  return (
    <SelectPagination
      label={
        isRequired
          ? t("booking.booking-form.program-selector.label")
          : t("booking.booking-form.program-selector.label-optional")
      }
      error={preferences?.program?.error ? t("booking.booking-form.program-selector.error") : null}
      placeholder={t("booking.booking-form.program-selector.placeholder")}
      loadOptions={loadProgramOptions}
      className={cx(css.preferencesContent_select, "col_4", "col_sm_12", className)}
      value={valueProgram?.label | valueProgram?.value ? valueProgram : null}
      debounceTimeout={1000}
      maxMenuHeight={maxMenuHeight}
      isClearable={true}
      onChange={(selectedProgram: any) => {
        if (selectedProgram) {
          setValueProgram(selectedProgram);
          preferences.program = selectedProgram;

          dispatch({ type: Types.SetPreferences, payload: preferences });
        } else {
          setValueProgram(null);
          const preferences = { ...state?.search?.preferences };
          preferences.program = selectedProgram?.program;

          dispatch({ type: Types.SetPreferences, payload: preferences });
        }
      }}
    />
  );
};

export default ProgramSelect;
